import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import { push } from 'connected-react-router';

import {
    useDeleteDistributorCompanyMutation,
    useGetDistributorCompanyEnrichedQuery,
    useGetInvestmentFundsQuery,
    useGetUserQuery,
    useImpersonateUserMutation,
} from 'api/main';
import { tunnelSelectors } from 'reducers';
import { notificationDialogOpen } from 'reducers/appNotification';
import { jsonParseSafe, parseFullName, saveInStorage } from 'utils/helpers';
import { useMatchAtLeastOneRole, useUserRoleMatchCategory } from 'utils/hooks';
import {
    RoleNames,
    stepIDs,
    distributorType,
    subscriptionStatus,
    listingActionButtonStyle,
    roleCategories,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import DocumentsSection from './DocumentsSection';
import ConventionsSection from './ConventionsSection';
import CompanySection from './CompanySection';
import RepresentativeSection from './RepresentativeSection';
import ComplementsSesction from './ComplementsSesction';
import MessagesSection from './MessagesSection';
import ImportDialog from './ImportDialog';

import { ConfirmDialog, Loader, ActionButton } from 'components';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';
import CommunicationSection from './CommunicationSection';

const useStyles = makeStyles(theme => ({
    return: {
        flex: '0 1',
        fontSize: 13,
        lineHeight: 1,
        display: 'inline-flex',
        cursor: 'pointer',
        alignItems: 'center',
        color: theme.palette.grey[700],
        textTransform: 'uppercase',
    },
    navIcon: {
        fontSize: 16,
        marginRight: theme.spacing(1),
    },
    action: {
        color: theme.palette.primary.main,
        textTransform: 'uppercase',
        lineHeight: 1,
        padding: theme.spacing(0, 2),
        cursor: 'pointer',

        '&:not(:last-child)': {
            borderRight: `1px solid ${theme.palette.grey[700]}`,
        },
    },
    breadcrum: {
        marginBottom: 5,
    },
}));

const DistributorDetails = () => {
    const mounted = useRef(false);
    const { id } = useParams();
    const classes = useStyles();
    const location = useLocation();
    const dispatch = useDispatch();
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();
    const [impersonateUser, { isLoading: impersonateLoader }] = useImpersonateUserMutation({
        fixedCacheKey: 'user-impersionation',
    });
    const {
        data: {
            0: distributor,
            nrOngoingSubscriptions: ongoingSubscriptionNumber,
            nrFinishedSubscriptions: finishedSubscriptionsNumber,
        } = {
            0: { user: {} },
            nrOngoingSubscriptions: -1,
        },
        isLoading: distributorLoading,
        isSuccess: distributorLoaded,
        isError,
        refetch,
    } = useGetDistributorCompanyEnrichedQuery(id, {
        skip: !id,
    });
    const { data: distributorMainUser, isLoading: distributorUserLoader } = useGetUserQuery(
        { id: distributor?.user?.id },
        { skip: !distributor?.user?.id }
    );
    const [deleteDistributorCompany, { isLoading: deleteLoader, isSuccess: deleteSuccessful }] =
        useDeleteDistributorCompanyMutation();

    const dataLoader = useSelector(tunnelSelectors.getDataLoader);

    const [impersonateOpen, setImpersonateOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    const subscription = useMemo(
        () => distributorMainUser?.subscriptions?.[0],
        [distributorMainUser]
    );

    const isAdminCIF = subscription?.role?.name === RoleNames.ADMIN_CIF;
    const isPSI = subscription?.role?.name === RoleNames.PSI;

    const canValidateDocuments = useMemo(
        () =>
            (subscription?.userTunnelSteps?.find(
                uts => uts.tunnelStep.step.id === stepIDs.DISTRIBUTOR_DOCUMENTS
            ) ||
                subscription?.subscriptionStatus === subscriptionStatus.FINISHED) &&
            distributor?.type === distributorType.STANDARD &&
            userIsAdminOrSignatory,
        [subscription, distributor, userIsAdminOrSignatory]
    );

    const complyAdvantageStatus = useMemo(() => {
        const uts = subscription?.userTunnelSteps?.find(
            uts => uts.tunnelStep.step.id === stepIDs.ACCOUNT_CREATION
        );
        const alert = uts?.alerts?.find(
            alert => alert.message === 'comply_advantage.search_distributor'
        );
        return jsonParseSafe(alert?.additionalInfo, null);
    }, [subscription]);

    const canEditMissingInfo = useMemo(
        () =>
            (subscription?.userTunnelSteps?.find(
                uts => uts.tunnelStep.step.id === stepIDs.DISTRIBUTOR_DOCUMENTS
            ) ||
                subscription?.subscriptionStatus === subscriptionStatus.FINISHED) &&
            userIsAdminOrSignatory,
        [subscription, userIsAdminOrSignatory]
    );

    const goTo = useCallback(
        path => {
            dispatch(push(path));
        },
        [dispatch]
    );

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleSendMessage = useCallback(() => {
        dispatch(notificationDialogOpen(distributor?.user));
    }, [dispatch, distributor?.user]);

    const handleDelete = useCallback(() => {
        deleteDistributorCompany(+id);
    }, [id, deleteDistributorCompany]);

    const handleBreadcrumb = useCallback(
        id => e => {
            e.preventDefault();
            if (!id) {
                goTo('/distributors');
            }
        },
        [goTo]
    );

    const handleImportOpen = useCallback(() => setImportOpen(true), []);

    const handleImpersonate = useCallback(() => {
        saveInStorage('route', location.pathname);
        impersonateUser({ userId: distributor?.user?.id, followupPath: '/homepage' });
    }, [impersonateUser, location, distributor]);

    useEffect(() => {
        if (isError || deleteSuccessful) goTo('/distributors');
    }, [isError, deleteSuccessful, goTo]);

    useEffect(() => {
        if ((distributorLoaded && !distributor?.user?.id) || isError) {
            dispatch(push('/distributors'));
        }
    }, [distributorLoaded, distributor, dispatch, isError]);

    return (
        <Loader
            loading={dataLoader || distributorLoading || distributorUserLoader || !distributor}
            marginTop={20}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Breadcrumbs
                    className={classes.breadcrum}
                    separator={<icons.MenuRight />}
                    aria-label="breadcrumb"
                >
                    <Link underline="hover" variant="h5" href="/" onClick={handleBreadcrumb()}>
                        {texts.DISTRIBUTEUR_LIST_TITLE}
                    </Link>
                    <Link underline="hover" variant="h5" href="/" onClick={handleBreadcrumb(id)}>
                        {distributor?.corporateName}
                    </Link>
                </Breadcrumbs>
                <Box display="flex" alignItems="flex-end" flexDirection="column">
                    {userIsAdminOrSignatory && (
                        <ActionButton
                            variant="outlined"
                            sx={listingActionButtonStyle}
                            disabled={distributor?.usersImportInProgress}
                            onClick={handleImportOpen}
                        >
                            {texts.IMPORT}
                        </ActionButton>
                    )}
                </Box>
            </Box>
            <ConfirmDialog
                open={confirmDeleteOpen}
                title={texts.SUPPRIMER}
                loading={deleteLoader}
                handleClose={() => setConfirmDeleteOpen(false)}
                handleConfirm={handleDelete}
            >
                {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
            </ConfirmDialog>
            <ConfirmDialog
                open={impersonateOpen}
                loading={impersonateLoader}
                title={texts.CONNECT_AS('distributor')}
                handleClose={() => setImpersonateOpen(false)}
                handleConfirm={handleImpersonate}
            >
                {texts.VOULOIR_VOUS_CONNECTER} {parseFullName(distributor?.user) + '\xa0?'}
            </ConfirmDialog>
            <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
            <CompanySection
                canEditMissingInfo={canEditMissingInfo}
                distributor={distributor}
                complyAdvantageStatus={complyAdvantageStatus}
            />
            <RepresentativeSection
                canEditMissingInfo={canEditMissingInfo}
                distributor={distributor}
                onSendMessage={handleSendMessage}
            />
            {(userIsPromoter || userIsServicer) && (
                <CommunicationSection distributor={distributor} />
            )}
            <ComplementsSesction
                distributor={distributor}
                canEditMissingInfo={canEditMissingInfo}
            />
            <ConventionsSection
                isCommercialisateur={isAdminCIF || isPSI}
                loader={fundsLoader || distributorLoading}
                fundList={fundList}
                company={distributor}
            />
            <DocumentsSection
                distributor={distributor}
                refetch={refetch}
                canAddDocument={userIsAdminOrSignatory}
                canValidateDocuments={canValidateDocuments}
                disableNewComment={!userIsNetworkAdmin && !userIsAdminOrSignatory}
            />
            <MessagesSection distributor={distributor} />
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography onClick={() => goTo('/distributors')} className={classes.return}>
                    <icons.ArrowLeft className={classes.navIcon} /> {texts.RETOUR}
                </Typography>
                {(userIsNetworkAdmin || userIsAdminOrSignatory) && (
                    <Box display="flex" alignItems="center">
                        {distributor?.user?.emailConfirmed && (
                            <Typography
                                className={classes.action}
                                onClick={() => setImpersonateOpen(true)}
                            >
                                {texts.IMPERSONATE_DISTRIBUTOR}
                            </Typography>
                        )}
                        {userIsAdminOrSignatory && (
                            <Typography onClick={handleSendMessage} className={classes.action}>
                                {texts.ENVOYER_UN_MESSAGE}
                            </Typography>
                        )}
                        {userIsAdminOrSignatory &&
                            +ongoingSubscriptionNumber === 0 &&
                            +finishedSubscriptionsNumber === 0 && (
                                <Typography
                                    onClick={() => setConfirmDeleteOpen(true)}
                                    className={classes.action}
                                >
                                    {texts.SUPPRIMER}
                                </Typography>
                            )}
                    </Box>
                )}
            </Box>
        </Loader>
    );
};

export default DistributorDetails;
